import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { ActionButtons, Flex, Input } from '@hcs/design-system';
import { InputLayout } from '@hcs/forms';
import { SponsorFormFields } from '@hcs/types';

import {
  useSponsorCreate,
  useSponsorInternal,
  useSponsorUpdate,
} from '../../hooks';

import { SponsorLogo } from './SponsorLogo';
import { SponsorPlace } from './SponsorPlace';

const dataHcName = 'edit-sponsor-form';
const MAX_NAME_LENGTH = 100;
const MAX_NAME_SLUG = 50;
const MAX_DISPLAY_NAME_LENGTH = 128;
const MAX_ABBREVIATION_LENGTH = 16;
const REQUIRED_MESSAGE = 'This field is required.';
const maxFieldSizeMessage = (fieldName: string, maxSize: number) =>
  `${fieldName} must be at most ${maxSize} characters.`;
const DEFAULT_FORM_VALUES: SponsorFormFields = {
  name: null,
  slug: null,
  logoUrl: null,
  displayName: null,
  abbreviation: null,
  hcMlsId: null,
  placeId: null,
};
export interface SponsorFormProps {
  sponsorId?: string;
  actionPortalIdRender?: string;
  onCancel: VoidFunction;
  onSubmit?: VoidFunction;
}

const yupSchema = yup.object().shape({
  name: yup
    .string()
    .max(MAX_NAME_LENGTH, maxFieldSizeMessage('Last Name', MAX_NAME_LENGTH))
    .required(REQUIRED_MESSAGE),
  slug: yup
    .string()
    .max(MAX_NAME_SLUG, maxFieldSizeMessage('Slug', MAX_NAME_SLUG))
    .required(REQUIRED_MESSAGE),
  displayName: yup
    .string()
    .max(
      MAX_DISPLAY_NAME_LENGTH,
      maxFieldSizeMessage('Display Name', MAX_DISPLAY_NAME_LENGTH),
    ),
  abbreviation: yup
    .string()
    .max(
      MAX_ABBREVIATION_LENGTH,
      maxFieldSizeMessage('Abbreviation', MAX_ABBREVIATION_LENGTH),
    ),
  hcMlsId: yup
    .number()
    .nullable()
    .typeError('HC Mls ID must be a number')
    .integer('HC Mls ID must be an integer')
    .positive('HC Mls ID must be a positive number'),
  placeId: yup
    .number()
    .nullable()
    .typeError('Place ID must be a number')
    .integer('Place ID must be an integer')
    .positive('Place ID must be a positive number'),
});
export const SponsorForm = ({
  sponsorId,
  actionPortalIdRender,
  onSubmit,
  onCancel,
}: SponsorFormProps) => {
  const { data: sponsor } = useSponsorInternal(sponsorId);
  const { mutate: createSponsor } = useSponsorCreate();
  const { mutate: updateSponsor } = useSponsorUpdate();

  const formValues: SponsorFormFields = sponsor
    ? {
        name: sponsor.name,
        slug: sponsor.slug,
        logoUrl: sponsor.logoUrl || null,
        displayName: sponsor.displayName || null,
        abbreviation: sponsor.abbreviation || null,
        hcMlsId: sponsor.hcMlsId || null,
        placeId: sponsor.placeId || null,
      }
    : DEFAULT_FORM_VALUES;

  const form = useForm<SponsorFormFields>({
    resolver: yupResolver(yupSchema),
    defaultValues: DEFAULT_FORM_VALUES,
    values: formValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });
  const { handleSubmit: formHandleSubmit, formState, control } = form;
  const { errors } = formState;
  const handleSubmit = formHandleSubmit(({ name, slug, ...values }) => {
    if (name && slug) {
      if (sponsorId) {
        updateSponsor({
          sponsorId,
          name,
          slug,
          displayName: values.displayName || undefined,
          abbreviation: values.abbreviation || undefined,
          logoUrl: values.logoUrl || undefined,
          hcMlsId: values.hcMlsId || undefined,
          placeId: values.placeId || undefined,
        });
      } else {
        createSponsor({
          name,
          slug,
          displayName: values.displayName || undefined,
          abbreviation: values.abbreviation || undefined,
          logoUrl: values.logoUrl || undefined,
          hcMlsId: values.hcMlsId || undefined,
          placeId: values.placeId || undefined,
        });
      }
      onSubmit?.();
    }
  });
  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit}>
        <Flex
          fullWidth
          direction="column"
          alignItems="stretch"
          style={{ paddingBottom: '20px' }}
        >
          <InputLayout label="Sponsor Name" required>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  dataHcName={`${dataHcName}-name-input`}
                  value={field.value || ''}
                  error={errors[field.name]?.message}
                />
              )}
            />
          </InputLayout>
          <InputLayout label="Sponsor Slug" required>
            <Controller
              name="slug"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  dataHcName={`${dataHcName}-slug-input`}
                  value={field.value || ''}
                  error={errors[field.name]?.message}
                />
              )}
            />
          </InputLayout>
          <InputLayout label="Display Name" required={false}>
            <Controller
              name="displayName"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  dataHcName={`${dataHcName}-display-name-input`}
                  value={field.value || ''}
                  error={errors[field.name]?.message}
                />
              )}
            />
          </InputLayout>
          <InputLayout label="Abbreviation" required={false}>
            <Controller
              name="abbreviation"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  dataHcName={`${dataHcName}-abbreviation-input`}
                  value={field.value || ''}
                  error={errors[field.name]?.message}
                />
              )}
            />
          </InputLayout>
          <InputLayout label="HC Mls ID" required={false}>
            <Controller
              name="hcMlsId"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  dataHcName={`${dataHcName}-hc-mls-id-input`}
                  value={field.value?.toString() || ''}
                  type="number"
                  error={errors[field.name]?.message}
                />
              )}
            />
          </InputLayout>
          <SponsorPlace savedPlaceId={sponsor?.placeId?.toString()} />
          <SponsorLogo dataHcName={`${dataHcName}-logoUrl`} />
          <ActionButtons
            portalIdRender={actionPortalIdRender}
            actions={[
              {
                label: 'Cancel',
                secondary: true,
                onClick: onCancel,
                dataHcName: `${dataHcName}-cancel-button`,
              },
              {
                label: 'Save',
                primary: true,
                onClick: handleSubmit,
                dataHcName: `${dataHcName}-save-button`,
              },
            ]}
          />
        </Flex>
      </form>
    </FormProvider>
  );
};
